import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, X, Trash2, ShieldCheck, Clock } from "lucide-react";

const STATUS = {
  pending: { label: "Čeká na schválení", cls: "border-amber-400/50 text-amber-600 bg-amber-50" },
  approved: { label: "Schválen", cls: "border-primary/40 text-primary bg-primary/5" },
  rejected: { label: "Zamítnut", cls: "border-destructive/40 text-destructive bg-destructive/5" },
};

export default function Admin() {
  const [users, setUsers] = useState([]);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { await fn(); toast.success(msg); load(); };
  const pending = users.filter((u) => u.status === "pending");

  return (
    <div className="fade-up">
      <PageHeader title="Správa uživatelů" subtitle="Schvalování registrací a správa přístupů">
        {pending.length > 0 && (
          <Badge className="bg-amber-500 hover:bg-amber-500" data-testid="admin-pending-count">
            <Clock className="h-3.5 w-3.5 mr-1" /> {pending.length} ke schválení
          </Badge>
        )}
      </PageHeader>

      <div className="px-10 py-8">
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60">
                <TableHead>Jméno</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead className="w-28">Role</TableHead>
                <TableHead className="w-44">Stav</TableHead>
                <TableHead className="w-32">Registrace</TableHead>
                <TableHead className="w-48 text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.role === "admin" ? "border-secondary/40 text-secondary bg-secondary/5" : ""}>
                      {u.role === "admin" ? <><ShieldCheck className="h-3 w-3 mr-1" /> Admin</> : "Uživatel"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS[u.status]?.cls}>{STATUS[u.status]?.label || u.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 justify-end">
                      {u.status !== "approved" && (
                        <Button size="sm" variant="outline" className="h-8 text-primary border-primary/30"
                          data-testid={`admin-approve-${u.id}`}
                          onClick={() => act(() => api.post(`/admin/users/${u.id}/approve`), "Uživatel schválen")}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Schválit
                        </Button>
                      )}
                      {u.status === "pending" && (
                        <Button size="sm" variant="outline" className="h-8 text-destructive border-destructive/30"
                          data-testid={`admin-reject-${u.id}`}
                          onClick={() => act(() => api.post(`/admin/users/${u.id}/reject`), "Registrace zamítnuta")}>
                          <X className="h-3.5 w-3.5 mr-1" /> Zamítnout
                        </Button>
                      )}
                      {u.role !== "admin" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="text-muted-foreground hover:text-destructive p-1.5" data-testid={`admin-delete-${u.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Smazat uživatele?</AlertDialogTitle>
                              <AlertDialogDescription>„{u.email}" bude odstraněn.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Zrušit</AlertDialogCancel>
                              <AlertDialogAction onClick={() => act(() => api.delete(`/admin/users/${u.id}`), "Uživatel smazán")}>Smazat</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
